import { useEffect, useState } from 'react';
import { Lesson } from 'pages/Education/Education.types';
import { DEFAULT_HEADERS } from 'src/shared/constants';

export const scheduleDays = [
	'SUNDAY',
	'MONDAY',
	'TUESDAY',
	'WEDNESDAY',
	'THURSDAY',
	'FRIDAY',
	'SATURDAY'
];

const useLessonsSchedule = (period?: { startDate?: string, endDate?: string }) => {
	const [buttonLabels, setButtonLabels] = useState<string[]>([]);
	const [allLessonsByDays, setAllLessonsByDays] = useState<Lesson[][]>([]);
	const [filteredLessonsByDay, setFilteredLessonsByDay] = useState<Lesson[][]>([]);
	const [scheduledHours, setScheduledHours] = useState<string[]>([]);
	const token = localStorage.getItem('token');

	useEffect(() => {
		const getData = async () => {
			const params = new URLSearchParams();
			params.set('start', period?.startDate ?? '');
			params.set('end', period?.endDate ?? '');
			const data = await fetch(`${import.meta.env.VITE_API_URL}/lessons-schedule?${params.toString()}`, {
				method: 'GET',
				headers: {
					...DEFAULT_HEADERS,
					Authorization: `Bearer ${token}`,
				},
			});
			const jsonData: Lesson[] = await data.json();
			const processedJson: Record<string, any> = jsonData.reduce((record, lesson) => {
				const day = lesson.dayOfWeek;
				if (!record[day]) {
					record[day] = [] as Lesson[];
				}
				record[day] = [...record[day], lesson];
				return record;
			}, {} as Record<string, Lesson[]>);

			const lessonsByDays = scheduleDays
				.map((day) => {
					if (processedJson[day]) {
						return processedJson[day] as Lesson[];
					}
					return [] as Lesson[];
				}) // get lesson schedule in the right day order
				.filter(l => !!l)
				.map((dayLessons) => dayLessons.sort((a: Lesson, b: Lesson) => { // sort day schedule by time
					if (a.startTime < b.startTime) {
						return -1;
					}
					if (a.startTime > b.startTime) {
						return 1;
					}
					return 0;
				}));
			const lessons = lessonsByDays.flat().sort((first, second) => {
				if (first.startTime < second.startTime) {
					return -1;
				}
				if (first.startTime > second.startTime) {
					return 1;
				}
				return 0;
			});
			setAllLessonsByDays(lessonsByDays);
			const minMaxAges = lessons.map((l) => ({
				minAge: l.minAge,
				maxAge: l.maxAge,
				minMax: `${l.minAge}-${l.maxAge}`,
				startTime: l.startTime,
			}));
			const ageLabels: any = {};
			const scheduleStartHours: any = {};
			minMaxAges.map((age) => {
				ageLabels[age.minMax] = age;
				scheduleStartHours[age.startTime] = age.startTime;
				return age;
			});
			setScheduledHours(Object.values(scheduleStartHours));
			const ageButtons = Object.keys(ageLabels).sort((first, second) => {
				if (parseInt(first.split('-')[0], 10) < parseInt(second.split('-')[0], 10)) {
					return -1;
				}
				if (parseInt(first.split('-')[0], 10) > parseInt(second.split('-')[0], 10)) {
					return 1;
				}
				return 0;
			});
			const initialAgeRange = minMaxAges.filter((age) => age.minMax === ageButtons[0]);
			setFilteredLessonsByDay(
				lessonsByDays
					.map((dayLessons) => dayLessons
						.filter(
							(lesson) => lesson.minAge === initialAgeRange[0].minAge
								&& lesson.maxAge === initialAgeRange[0].maxAge,
						)),
			);
			setButtonLabels(ageButtons);
		};
		if (period?.startDate && period?.endDate) {
			getData().then();
		}
	}, [period?.startDate, period?.endDate]);

	return {
		buttonLabels,
		allLessonsByDays,
		filteredLessonsByDay,
		scheduledHours,
	};
};

export default useLessonsSchedule;
